import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { getClientBalance } from "api/client.api";

const PAYMENT_MODES = [
  "Cash",
  "Bank Deposit",
  "Cheque",
  "Credit Card",
  "Debit Card",
  "Demand Draft",
  "Online Payment Gateway",
  "Online Transfer",
  "PayPal",
  "Wire Transfer",
];

const EMPTY_FORM = {
  clientId: "",
  refundDate: new Date().toISOString().slice(0, 10),
  paymentMode: "Bank Deposit",
  amount: "",
  refundFrom: "",
  reference: "",
  reason: "",
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-SA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const AddRefundModal = ({ clients = [], onSave, onClose, isSaving }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [balance, setBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const availableCredit = useMemo(() => {
    const balanceDue = Number(balance?.balanceDue || 0);
    return Math.max(0, -balanceDue);
  }, [balance]);

  const refundAmount = Number(form.amount || 0);

  useEffect(() => {
    const loadBalance = async () => {
      if (!form.clientId) {
        setBalance(null);
        return;
      }

      setIsLoadingBalance(true);

      try {
        const response = await getClientBalance(form.clientId);
        setBalance(response?.balance || null);
      } catch {
        setBalance(null);
      } finally {
        setIsLoadingBalance(false);
      }
    };

    loadBalance();
  }, [form.clientId]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e = {};

    if (!form.clientId) e.clientId = "Client is required.";
    if (!form.refundDate) e.refundDate = "Refund date is required.";
    if (!form.paymentMode) e.paymentMode = "Refund mode is required.";

    if (!form.amount) {
      e.amount = "Amount is required.";
    } else if (Number.isNaN(refundAmount) || refundAmount <= 0) {
      e.amount = "Amount must be greater than zero.";
    } else if (refundAmount > availableCredit) {
      e.amount = `Amount cannot exceed available credit of SR ${money(
        availableCredit,
      )}.`;
    }

    if (availableCredit <= 0 && form.clientId) {
      e.clientId = "This client has no refundable credit.";
    }

    return e;
  };

  const handleSave = () => {
    const e = validate();

    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    onSave({
      clientId: form.clientId,
      refundDate: form.refundDate,
      paymentMode: form.paymentMode,
      amount: refundAmount,
      refundFrom: form.refundFrom.trim() || null,
      reference: form.reference.trim() || null,
      reason: form.reason.trim() || null,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 16 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="RotateCcw" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Record Client Refund
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Refund available client credit back to the customer.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Client <span className="text-red-500">*</span>
            </label>

            <select
              value={form.clientId}
              onChange={(event) => set("clientId", event.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.clientId ? "border-red-500" : "border-border"
              }`}
            >
              <option value="">Select client</option>
              {clients.map((client) => (
                <option
                  key={client.id || client._id}
                  value={client.id || client._id}
                >
                  {client.name}
                </option>
              ))}
            </select>

            {errors.clientId && (
              <p className="text-xs text-red-500 mt-1">{errors.clientId}</p>
            )}
          </div>

          {form.clientId && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">
                  Current Balance
                </div>
                <div className="font-semibold text-foreground">
                  {isLoadingBalance
                    ? "Loading..."
                    : `SR ${money(balance?.balanceDue)}`}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">
                  Available Credit
                </div>
                <div
                  className={`font-semibold ${
                    availableCredit > 0
                      ? "text-green-600 dark:text-green-400"
                      : "text-muted-foreground"
                  }`}
                >
                  SR {money(availableCredit)}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">
                  Meaning
                </div>
                <div className="font-semibold text-foreground text-sm">
                  {availableCredit > 0
                    ? "You owe client"
                    : "No refundable credit"}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Refund Date <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={form.refundDate}
                onChange={(event) => set("refundDate", event.target.value)}
                className={errors.refundDate ? "border-red-500" : ""}
              />
              {errors.refundDate && (
                <p className="text-xs text-red-500 mt-1">{errors.refundDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="0"
                max={availableCredit}
                step="0.01"
                value={form.amount}
                onChange={(event) => set("amount", event.target.value)}
                placeholder="0.00"
                className={errors.amount ? "border-red-500" : ""}
              />
              {errors.amount && (
                <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Refund Mode <span className="text-red-500">*</span>
            </label>

            <select
              value={form.paymentMode}
              onChange={(event) => set("paymentMode", event.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Refund From"
              value={form.refundFrom}
              onChange={(event) => set("refundFrom", event.target.value)}
              placeholder="e.g. Main bank account"
            />

            <Input
              label="Reference"
              value={form.reference}
              onChange={(event) => set("reference", event.target.value)}
              placeholder="e.g. REF-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reason
            </label>
            <textarea
              value={form.reason}
              onChange={(event) => set("reason", event.target.value)}
              rows={3}
              placeholder="Reason for this refund..."
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Available credit:{" "}
            <span className="font-semibold text-foreground">
              SR {money(availableCredit)}
            </span>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="default"
              size="md"
              iconName="RotateCcw"
              onClick={handleSave}
              disabled={isSaving || availableCredit <= 0}
            >
              {isSaving ? "Saving..." : "Record Refund"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddRefundModal;
